import { useEffect, useState } from 'react';

import { Button, Loader, Modal, Radio, Text, Textarea, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';

import {
  useGetTriageAuditLogQuery,
  useSubmitTriageReviewMutation
} from '@/store/slices/api/transmissionReport/transmissionReportsApiSlice';

import styles from './TriageAuditLogModal.module.scss';

interface TriageAuditLogModalProps {
  opened: boolean;
  onClose: () => void;
  triageAuditLogId: string;
}

interface TriggerReview {
  isCorrect: boolean | null;
  notes: string;
}

const TriageAuditLogModal = ({ opened, onClose, triageAuditLogId }: TriageAuditLogModalProps) => {
  const { data, isLoading, isError } = useGetTriageAuditLogQuery(triageAuditLogId, {
    skip: !opened || !triageAuditLogId
  });
  const [submitReview, { isLoading: isSubmitting }] = useSubmitTriageReviewMutation();

  const log = data?.triageAuditLog;

  const [triggerReviews, setTriggerReviews] = useState<Record<string, TriggerReview>>({});

  useEffect(() => {
    if (log) {
      const initial: Record<string, TriggerReview> = {};
      Object.keys(log.response).forEach((id) => {
        const existing = log.review?.[id];
        initial[id] = {
          isCorrect: existing ? existing.isCorrect : true,
          notes: existing?.notes ?? ''
        };
      });
      setTriggerReviews(initial);
    }
  }, [log]);

  const setReviewField = (triggerId: string, field: keyof TriggerReview, value: boolean | string) => {
    setTriggerReviews((prev) => ({
      ...prev,
      [triggerId]: { ...prev[triggerId], [field]: value }
    }));
  };

  const handleSubmit = async () => {
    const body: Record<string, { isCorrect: boolean; notes?: string }> = {};
    Object.entries(triggerReviews).forEach(([id, review]) => {
      if (review.isCorrect === null && !review.notes.trim()) return;
      const entry: { isCorrect: boolean; notes?: string } = {
        isCorrect: review.isCorrect ?? true
      };
      if (review.notes.trim()) entry.notes = review.notes.trim();
      body[id] = entry;
    });

    try {
      await submitReview({ triageAuditLogId, body }).unwrap();
      notifications.show({ message: 'Review submitted successfully', color: 'green' });
      onClose();
    } catch {
      notifications.show({ message: 'Failed to submit review. Please try again.', color: 'red' });
    }
  };

  const triggers = log ? Object.entries(log.response) : [];
  const sortedTriggers = [
    ...triggers.filter(([, t]) => t.triggered),
    ...triggers.filter(([, t]) => !t.triggered)
  ];

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      radius="md"
      padding={0}
      withCloseButton
      centered
      title={
        <Title order={5} c="white" fw={600}>
          AI Triage Evaluation
        </Title>
      }
      classNames={{ header: styles.header }}>

      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
          <Loader color="plum" />
        </div>
      )}

      {isError && (
        <div style={{ padding: '32px 20px' }}>
          <Text c="red.6" ta="center">Failed to load triage evaluation. Please try again.</Text>
        </div>
      )}

      {log && (
        <>
          <div className={styles.metaRow}>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Outcome</span>
              <span className={styles.metaValue}>{log.outcome}</span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Evaluated at</span>
              <span className={styles.metaValue}>
                {dayjs(log.createdAt).format('MMM D, YYYY h:mm A')}
              </span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Processing time</span>
              <span className={styles.metaValue}>
                {(log.processingTimeMs / 1000).toFixed(1)}s
              </span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Input tokens</span>
              <span className={styles.metaValue}>{log.inputTokens.toLocaleString()}</span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Output tokens</span>
              <span className={styles.metaValue}>
                {(log.outputTokens + log.thoughtTokens).toLocaleString()}
              </span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Model</span>
              <span className={styles.metaValue}>{log.modelName}</span>
            </div>
            {log.reviewedAt && (
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Reviewed at</span>
                <span className={styles.metaValue}>
                  {dayjs(log.reviewedAt).format('MMM D, YYYY h:mm A')}
                </span>
              </div>
            )}
          </div>

          <div className={styles.body}>
            <div className={styles.sectionTitle}>Triggers</div>
            <div className={styles.triggerList}>
              {sortedTriggers.map(([triggerId, trigger]) => (
                <div key={triggerId} className={styles.triggerItem}>
                  <span className={styles.triggerIcon}>
                    {trigger.triggered ? '✅' : '❌'}
                  </span>
                  <span className={styles.triggerText}>{trigger.text}</span>
                  <span className={styles.triggerExplanation}>{trigger.explanation}</span>

                  <div className={styles.reviewSection}>
                    <Radio.Group
                      value={triggerReviews[triggerId]?.isCorrect ? 'correct' : 'incorrect'}
                      onChange={(val) => setReviewField(triggerId, 'isCorrect', val === 'correct')}>
                      <div className={styles.radioRow}>
                        <Radio value="correct" label="Correct" color="plum.9" />
                        <Radio value="incorrect" label="Incorrect" color="plum.9" />
                      </div>
                    </Radio.Group>
                    <Textarea
                      placeholder="Notes (Optional)"
                      value={triggerReviews[triggerId]?.notes ?? ''}
                      onChange={(e) => setReviewField(triggerId, 'notes', e.currentTarget.value)}
                      autosize
                      minRows={1}
                      maxRows={4}
                      classNames={{ input: styles.notesInput }}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.sectionTitle}>Thoughts</div>
            <div
              className={styles.thoughtsArea}
              dangerouslySetInnerHTML={{ __html: formatThoughts(log.thoughts) }}
            />
          </div>

          <div className={styles.footer}>
            <Button variant="outline" color="plum.9" onClick={handleSubmit} loading={isSubmitting}>
              Submit Review
            </Button>
            <Button variant="filled" color="plum.9" onClick={onClose}>
              Close
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

function formatThoughts(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br/>');
}

export default TriageAuditLogModal;
